import { ToastContext, ToastTypeEnum } from "context/toastContext";
import {
  Company_Candidate_Type,
  CreateOrUpdateCompanyCandidateLoginMutationResult,
  CreateOrUpdateCompanyCandidateMutationResult,
  useCreateOrUpdateCompanyCandidateLoginMutation,
  useCreateOrUpdateCompanyCandidateMutation,
} from "@generated/graphql";
import { useContext } from "react";
import useIsCreate from "hooks/useIsCreate";
import useCreateExamination from "pages/Testing/view/hooks/useCreateExamination";
import { CandidateType } from "constants/global";
import { parseApolloError } from "helpers/errorsParser";
import { getExaminationSendResultType } from "pages/Testing/view/helpers";
import { InfoInputs, IUseTrialSubmitParams } from "../types";
import useUpdateTrial from "./useUpdateTrial";

type UpdateTelegramCompanyCandidateResult = NonNullable<
  CreateOrUpdateCompanyCandidateMutationResult["data"]
>["createOrUpdateCompanyCandidate"];
type UpdateLoginCompanyCandidateResult = NonNullable<
  CreateOrUpdateCompanyCandidateLoginMutationResult["data"]
>["createOrUpdateCompanyCandidateLogin"];

const useUpdateCandidate = ({ setIsEditing }: IUseTrialSubmitParams) => {
  const { addToast } = useContext(ToastContext);
  const isCreate = useIsCreate();

  const defaultCandidateErrorMessageText = "Ошибка сохранения кандидата";

  const [updateCompanyCandidate, { loading }] = useCreateOrUpdateCompanyCandidateMutation();

  const [updateCompanyCandidateLogin, { loading: updateCompanyCandidateLoginLoading }] =
    useCreateOrUpdateCompanyCandidateLoginMutation();

  const updateCompanyCandidateLoading = loading || updateCompanyCandidateLoginLoading;

  const { updateTrialLoading, onUpdateTrial } = useUpdateTrial();
  const { createExaminationLoading, onCreateExamination } = useCreateExamination();

  const handleCandidateResponse = (
    candidateRes: UpdateLoginCompanyCandidateResult | UpdateTelegramCompanyCandidateResult | undefined,
    formData: InfoInputs
  ) => {
    const candidateId = candidateRes?.id;
    if (candidateId && formData.competencyMap?.id) {
      if (isCreate) {
        onCreateExamination({
          competencyMapId: formData.competencyMap.id,
          title: formData.title || formData.competencyMap.title,
          personalInput: {
            companyCandidateId: candidateId,
          },
          isSendResult: formData.isSendResult,
          sendResultType: getExaminationSendResultType(formData.isExtendedReport),
        });
      } else {
        onUpdateTrial({ formData, candidateId, setIsEditing });
      }
    } else if (!candidateId) {
      console.error("no candidateId");
      addToast({ type: ToastTypeEnum.ERROR, text: defaultCandidateErrorMessageText });
    } else {
      console.error("no formData.comptencyMap.id");
      addToast({ type: ToastTypeEnum.ERROR, text: "Не выбрана карта компетенций" });
    }
  };

  const onUpdateCompanyCandidate = (formData: InfoInputs) => {
    const isTelegramCandidate = formData.candidateType === CandidateType.Telegram;

    if (isTelegramCandidate) {
      updateCompanyCandidate({
        variables: {
          input: {
            id: formData.candidate.id,
            email: formData.candidate.email,
            fullName: formData.candidate.fullName?.fullName || "",
            organization: formData.candidate?.organization,
            position: formData.candidate?.position,
            grade: formData.candidate?.grade?.id,
            telegramUsername: formData.candidate.telegramUsername?.telegramUsername?.replaceAll("@", "") || "",
            type: Company_Candidate_Type.Candidate,
          },
        },
      })
        .then((candidateRes) => handleCandidateResponse(candidateRes.data?.createOrUpdateCompanyCandidate, formData))
        .catch((e) => parseApolloError(e, { addToast, defaultText: defaultCandidateErrorMessageText }));
    } else {
      updateCompanyCandidateLogin({
        variables: {
          input: {
            id: formData.candidate.id,
            email: formData.candidate.email,
            fullName: formData.candidate.fullName?.fullName || "",
            organization: formData.candidate?.organization,
            position: formData.candidate?.position,
            grade: formData.candidate?.grade?.id,
            login: formData.candidate.login?.login || "",
            password: formData.password || "",
            type: Company_Candidate_Type.Candidate,
          },
        },
      })
        .then((candidateRes) =>
          handleCandidateResponse(candidateRes.data?.createOrUpdateCompanyCandidateLogin, formData)
        )
        .catch((e) => parseApolloError(e, { addToast, defaultText: defaultCandidateErrorMessageText }));
    }
  };

  return { onUpdateCompanyCandidate, updateCompanyCandidateLoading, updateTrialLoading, createExaminationLoading };
};

export default useUpdateCandidate;
